import { useBreakpoint } from 'components/BreakpointProvider';
import { FlexCol, FlexRowMdMax } from 'components/primitives/flex';
import useModal from 'components/remodal/v2/useModal';
import Button from 'components/ui/Button/Button';
import dayjs from 'dayjs';
import { Fragment } from 'react';
import styled from 'styled-components';
import { BoatReviews } from 'swagger/models';
import StarRating from './StarRating';
import { UserCardReviewer, UserCardReviewerSkeleton } from './UserCard';
import { ContainerGrid1to4, SubHeading } from './common';
import { useInfiniteBoatReviews } from './hooks';
import ReviewsModalContent from './modals/ReviewsModalContent';

const loadingUserCardSkeleton = <UserCardReviewerSkeleton />;

type Props = {
  isLoading: boolean;
  boatRating: number;
  hasNoReviews: boolean;
  boatId: string;
};

export default function Reviews({ isLoading, boatRating, hasNoReviews, boatId }: Props) {
  const breakpoints = useBreakpoint();
  const smallScreen = breakpoints.xs || breakpoints.sm || breakpoints.md;

  const { pages: reviews, reviewsTotal } = useInfiniteBoatReviews(boatId);

  const { modal, openModal } = useModal(<ReviewsModalContent boatId={boatId} boatRating={boatRating} />, {
    isOnRight: true,
    hideCloseButton: smallScreen,
    trackingName: 'reviews_modal_content',
  });

  if (!isLoading && hasNoReviews) {
    return null;
  }

  const renderReviewer = getRenderReviewer(openModal);

  const flatReviews = reviews.map(({ data }) => data).flat();

  const showButton = !isLoading && reviewsTotal > 6;

  return (
    <Container id="reviews" data-testid="component-reviews-and-rating-container">
      <LeftColumn>
        <HeadingContainer>
          <SubHeading>Ratings & reviews</SubHeading>
          <StarRating short countText="rating" reviewCount={isLoading ? 0 : reviewsTotal} rating={boatRating} />
        </HeadingContainer>
        {!smallScreen && showButton && <Button outline onClick={openModal} text={`SEE ALL ${reviewsTotal} REVIEWS`} />}
      </LeftColumn>

      {smallScreen ? (
        <ScrollContainer>
          {flatReviews.slice(0, 10)?.map((review) => (
            <Slide key={review.id} $oneReview={reviews?.length === 1}>
              {isLoading ? loadingUserCardSkeleton : renderReviewer(review)}
            </Slide>
          ))}
        </ScrollContainer>
      ) : (
        <ReviewsWrapper data-testid="component-reviews-and-ratings-reviews">
          {isLoading
            ? [...Array(6)].map((_, i) => <Fragment key={i}>{loadingUserCardSkeleton}</Fragment>)
            : flatReviews.slice(0, 6).map(renderReviewer)}
        </ReviewsWrapper>
      )}
      {smallScreen && showButton && (
        <StyledButton outline onClick={openModal} text={`SEE ALL ${reviewsTotal} REVIEWS`} />
      )}

      {modal}
    </Container>
  );
}

const getRenderReviewer =
  (toggleSidebar) =>
  ({ id, author, text, created_at, rating }: BoatReviews) =>
    (
      <UserCardReviewer
        key={id}
        profilePhotoUrl={author.picture.url}
        text={text}
        reviewerName={author.first_name}
        reviewDate={dayjs(created_at).format('MMM YYYY')}
        rating={rating}
        onReadMoreClick={toggleSidebar}
      />
    );

const Container = styled(ContainerGrid1to4)`
  scroll-margin-top: 70px;
  width: 100%;
  max-width: 100%;

  button {
    width: fit-content;
    padding: 0 22px;
  }
`;

const StyledButton = styled(Button)`
  width: 100% !important;
`;

const ReviewsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 42px 96px;
`;

const LeftColumn = styled(FlexCol)`
  justify-content: space-between;
`;

const HeadingContainer = styled(FlexRowMdMax)`
  justify-content: space-between;
`;

const ScrollContainer = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  gap: 24px;
  margin: 0 -21px;
  padding: 0 21px;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Slide = styled.div<{ $oneReview: boolean }>`
  flex-shrink: 0;
  scroll-snap-align: start;
  width: ${({ $oneReview }) => ($oneReview ? '100%' : '80%')};
`;
